import { useCallback, useEffect, useState } from 'react';
import { withRouter, RouteComponentProps } from 'react-router';
import { useTranslation } from 'react-i18next';
import {
  Spinner,
  PageWrapper,
  ComplexTable,
  Breadcrumbs,
  Tiles,
  ListTiles,
  ListTitle,
} from 'components';
import contractsService from 'services/contracts';
import { useFetch } from 'hooks';
import { useSelector } from 'react-redux';
import tStore from 'types/store';
import { tContract, tGetAllContractsAPI } from 'types/services/contracts';
import { ListBreadcrumb, ListTableContainer } from 'styles/GlobalStyledComponents';
import { tableHead } from './TableConfig';

interface iContractsList extends RouteComponentProps<any> {}

const ContractsList: React.FC<iContractsList> = ({ history }): JSX.Element => {
  const { t } = useTranslation();

  const permissions = useSelector((state: tStore) => state.user.permissions);
  const canWrite = !!permissions.includes('tunContractsWrite');

  const [limit, setLimit] = useState<number>(10);
  const [offset, setOffset] = useState<number>(0);
  const [search, setSearch] = useState<string>('');
  const [sort, setSort] = useState<string>('');

  const { fetch, loading, data, count } = useFetch<tContract, tGetAllContractsAPI>(
    contractsService.getAll,
    'Something went wrong. Try again',
  );

  const fetchData = useCallback(
    () => fetch({ search, limit, offset, ordering: sort }),
    [search, limit, offset, sort, fetch],
  );

  const setSortParams = (field: string, sort: string) => {
    const type: string = sort ? '-' : '';
    setSort(`${type}${field}`);
  };

  const setRequestParams = useCallback(
    (newOffset: number, newLimit: number) => {
      if (newOffset !== offset) setOffset(newOffset);
      if (newLimit !== limit) setLimit(newLimit);
    },
    [limit, offset],
  );

  const changeRoute = () => history.push('/contracts/new');

  const showContractsDetails = (id: number | string) => history.push(`/contracts/${id}`);

  useEffect(() => {
    setOffset(0);
  }, [search]);

  useEffect(() => {
    fetchData();
  }, [limit, offset, sort, fetchData]);

  return (
    <PageWrapper>
      {loading && <Spinner />}
      <ListBreadcrumb>
        <Breadcrumbs crumbs={[{ url: '/contracts', label: t('Contracts') }]} />
      </ListBreadcrumb>
      <ListTitle title={t('Contracts')} />
      <ListTiles>
        <Tiles
          text={t('Create new contract')}
          icon='description'
          bgIcon='addComment'
          color='orange'
          variant='filled'
          onClick={changeRoute}
          disabled={!canWrite}
        />
      </ListTiles>
      <ListTableContainer>
        <ComplexTable
          isRowClickable
          onRowClick={showContractsDetails}
          value={search}
          resetValue={() => setSearch('')}
          onChange={({ target: { value } }) => setSearch(value)}
          name='tableSearch'
          tableBodyData={data}
          tableHead={tableHead}
          setSortParams={(field: string, sort: string) => setSortParams(field, sort)}
          totalRecords={count}
          setParams={(offset: number, limit: number) => setRequestParams(offset, limit)}
          tableName='Contracts list'
          limit={limit}
          offset={offset}
        />
      </ListTableContainer>
    </PageWrapper>
  );
};

export default withRouter(ContractsList);
